import { ChangeEvent, useReducer, useState } from "react";
import { FileEditAltIcon, TextInput, ToggleInput } from "@datagame/ui";
import { useTranslation } from "react-i18next";

import {
  FeedbackPage,
  FeedbackPageUpdateInput,
  LibraryImage,
  LibraryImageKind,
} from "../../../../../../../gql/graphql";
import { ColorInput } from "../../../../../../shared/ui/ColorInput";
import { ImageInput, Section } from "../../Common";

import styles from "./FeedbackPageForm.module.scss";

export interface FeedbackPageFormProps {
  feedbackPage: FeedbackPage;
  onUpdate: (data: FeedbackPageUpdateInput) => void;
}

enum ActionTypes {
  updateTitle = "updateTitle",
  updateTitleColor = "updateTitleColor",
  updateInstructionLine = "updateInstructionLine",
  updateInstructionLineColor = "updateInstructionLineColor",
  updateTextBoxPrompt = "updateTextBoxPrompt",
  updateTextBoxPromptColor = "updateTextBoxPromptColor",
  updateSubmitButton = "updateSubmitButton",
  updateSubmitButtonColor = "updateSubmitButtonColor",
  updateBackgroundColor = "updateBackgroundColor",
  updateBackgroundImage = "updateBackgroundImage",
}

type Actions =
  | { type: ActionTypes.updateTitle; value: string }
  | { type: ActionTypes.updateTitleColor; value: string }
  | { type: ActionTypes.updateInstructionLine; value: string }
  | { type: ActionTypes.updateInstructionLineColor; value: string }
  | { type: ActionTypes.updateTextBoxPrompt; value: string }
  | { type: ActionTypes.updateTextBoxPromptColor; value: string }
  | { type: ActionTypes.updateSubmitButton; value: string }
  | { type: ActionTypes.updateSubmitButtonColor; value: string }
  | { type: ActionTypes.updateBackgroundColor; value: string }
  | { type: ActionTypes.updateBackgroundImage; image: LibraryImage | null };

type State = {
  title: string;
  titleColor: string;
  instructionLine: string;
  instructionLineColor: string;
  textBoxPrompt: string;
  textBoxPromptColor: string;
  submitButton: string;
  submitButtonColor: string;
  backgroundColor: string;
  backgroundImage: LibraryImage | null;
};

const reducer = (state: State, action: Actions) => {
  switch (action.type) {
    case ActionTypes.updateTitle: {
      return {
        ...state,
        title: action.value,
      };
    }
    case ActionTypes.updateTitleColor: {
      return {
        ...state,
        titleColor: action.value,
      };
    }
    case ActionTypes.updateInstructionLine: {
      return {
        ...state,
        instructionLine: action.value,
      };
    }
    case ActionTypes.updateInstructionLineColor: {
      return {
        ...state,
        instructionLineColor: action.value,
      };
    }
    case ActionTypes.updateTextBoxPrompt: {
      return {
        ...state,
        textBoxPrompt: action.value,
      };
    }
    case ActionTypes.updateTextBoxPromptColor: {
      return {
        ...state,
        textBoxPromptColor: action.value,
      };
    }
    case ActionTypes.updateSubmitButton: {
      return {
        ...state,
        submitButton: action.value,
      };
    }
    case ActionTypes.updateSubmitButtonColor: {
      return {
        ...state,
        submitButtonColor: action.value,
      };
    }
    case ActionTypes.updateBackgroundColor: {
      return {
        ...state,
        backgroundColor: action.value,
      };
    }
    case ActionTypes.updateBackgroundImage: {
      return {
        ...state,
        backgroundImage: action.image,
      };
    }
  }
};

export const FeedbackPageForm = ({ feedbackPage, onUpdate }: FeedbackPageFormProps) => {
  const { t } = useTranslation();

  const [state, dispatch] = useReducer(reducer, feedbackPage as State);
  const [isUsingImage, setIsUsingImage] = useState(Boolean(state.backgroundImage));

  const handleUpdate = () => {
    onUpdate({
      title: state.title,
      titleColor: state.titleColor,
      instructionLine: state.instructionLine,
      instructionLineColor: state.instructionLineColor,
      textBoxPrompt: state.textBoxPrompt,
      textBoxPromptColor: state.textBoxPromptColor,
      submitButton: state.submitButton,
      submitButtonColor: state.submitButtonColor,
      backgroundColor: state.backgroundColor,
    });
  };

  const handleUploadBackgroundImage = (image: LibraryImage) => {
    dispatch({ type: ActionTypes.updateBackgroundImage, image });

    onUpdate({ backgroundImageId: image.id });
  };

  const handleRemoveBackgroundImage = () => {
    dispatch({ type: ActionTypes.updateBackgroundImage, image: null });
    onUpdate({ backgroundImageId: null });
  };

  const handleIsUsingImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    if (!value) {
      handleRemoveBackgroundImage();
    }
    setIsUsingImage(event.target.checked);
  };

  const renderBackgroundSectionContent = () => {
    if (isUsingImage) {
      return (
        <ImageInput
          image={state.backgroundImage}
          kind={LibraryImageKind.Background}
          onUpload={handleUploadBackgroundImage}
          onRemove={handleRemoveBackgroundImage}
          showRemoveButton={true}
        />
      );
    }

    return (
      <ColorInput
        value={state.backgroundColor}
        onChange={(color) =>
          dispatch({ type: ActionTypes.updateBackgroundColor, value: color })
        }
        onBlur={handleUpdate}
      />
    );
  };

  return (
    <div className={styles.formWrapper}>
      <div className={styles.fieldWrapper}>
        <Section className={styles.section} title={t("project:feedbackPage.title")}>
          <TextInput
            size="small"
            name="title"
            icon={<FileEditAltIcon />}
            placeholder={t("project:feedbackPage.titlePlaceholder") ?? ""}
            value={state?.title}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateTitle, value: event.target.value })
            }
            onBlur={handleUpdate}
          />
        </Section>

        <Section className={styles.section} title={t("project:feedbackPage.titleColor")}>
          <ColorInput
            value={state.titleColor}
            onChange={(color) =>
              dispatch({ type: ActionTypes.updateTitleColor, value: color })
            }
            onBlur={handleUpdate}
          />
        </Section>
      </div>

      <div className={styles.fieldWrapper}>
        <Section className={styles.section} title={t("project:feedbackPage.instructionLine")}>
          <TextInput
            size="small"
            name="title"
            icon={<FileEditAltIcon />}
            placeholder={t("project:feedbackPage.instructionLinePlaceholder") ?? ""}
            value={state.instructionLine}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateInstructionLine, value: event.target.value })
            }
            onBlur={handleUpdate}
          />
        </Section>

        <Section className={styles.section} title={t("project:feedbackPage.instructionLineColor")}>
          <ColorInput
            value={state.instructionLineColor}
            onChange={(color) =>
              dispatch({ type: ActionTypes.updateInstructionLineColor, value: color })
            }
            onBlur={handleUpdate}
          />
        </Section>
      </div>

      <div className={styles.fieldWrapper}>
        <Section className={styles.section} title={t("project:feedbackPage.textboxPrompt")}>
          <TextInput
            size="small"
            name="title"
            icon={<FileEditAltIcon />}
            placeholder={t("project:feedbackPage.textboxPromptPlaceholder") ?? ""}
            value={state.textBoxPrompt}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateTextBoxPrompt, value: event.target.value })
            }
            onBlur={handleUpdate}
          />
        </Section>
        <Section className={styles.section} title={t("project:feedbackPage.textboxPromptColor")}>
          <ColorInput
            value={state.textBoxPromptColor}
            onChange={(color) =>
              dispatch({ type: ActionTypes.updateTextBoxPromptColor, value: color })
            }
            onBlur={handleUpdate}
          />
        </Section>
      </div>

      <div className={styles.fieldWrapper}>
        <Section className={styles.section} title={t("project:feedbackPage.submitButton")}>
          <TextInput
            size="small"
            name="title"
            icon={<FileEditAltIcon />}
            placeholder={t("project:feedbackPage.submitButtonPlaceholder") ?? ""}
            value={state.submitButton}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateSubmitButton, value: event.target.value })
            }
            onBlur={handleUpdate}
          />
        </Section>

        <Section className={styles.section} title={t("project:feedbackPage.submitButtonColor")}>
          <ColorInput
            value={state.submitButtonColor}
            onChange={(color) =>
              dispatch({ type: ActionTypes.updateSubmitButtonColor, value: color })
            }
            onBlur={handleUpdate}
          />
        </Section>
      </div>
      <Section
        className={styles.section}
        title={isUsingImage ? t("project:welcomeScreen.backgroundImage") : t("project:welcomeScreen.backgroundColor")}
      >
        {renderBackgroundSectionContent()}

        <ToggleInput
          className={styles.transferToggle}
          onChange={handleIsUsingImageChange}
          value={isUsingImage}
          label={t("project:gameResults.useImage") ?? ""}
          name="transfer"
        />
      </Section>
    </div>
  );
};
